// @ts-nocheck
import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { CompetitionScoreLevel, CompetitionUserScore } from '../userApi';
import { withLocalization, LProps } from '../utils/wrappers';
import UserScoreLevel from './UserScoreLevel';
import FootprintProfileControl from './FootprintProfileControl';
import { State } from '../redux/state';
import { estimateUserDollarSavings } from '../utils/actionSavings';

interface ManualProps {
  score: CompetitionUserScore;
  scoreLevel?: CompetitionScoreLevel;
  userUniversityRank?: number;
  pledgeCompletions: number;
  footprint: number;
  footprintPublic?: boolean;
  canManageFootprint?: boolean;
  onHideFootprint?: () => void;
  onRestoreFootprint?: () => void;
  nextScoreLevel: CompetitionScoreLevel;
  /** When set, use these completions for $ estimates (own profile vs viewed profile). */
  completionsSource?: 'user' | 'profile';
}

interface Props extends ManualProps {
  newDollarsSaved: number;
  takenDollarsSaved: number;
  showDollarSavings: boolean;
}

const formatDollars = (value: number, formatNumber: (n: number, d?: number) => string) =>
  `$${formatNumber(Math.round(value), 0)}`;

const UserStats = ({
  t, formatNumber, score, pledgeCompletions, footprint, footprintPublic = true,
  canManageFootprint = false, onHideFootprint, onRestoreFootprint, scoreLevel, nextScoreLevel,
  newDollarsSaved, takenDollarsSaved, showDollarSavings
}: LProps<Props>) => (
  <div className="cc__white_box">
    <div className="cc__stats__item">
      <div className="cc__stats__label">{t('Points')}</div>
      <div className="cc__lighttext">{formatNumber(score.points, 0)}</div>
    </div>
    <div className="cc__stats__item">
      <div className="cc__stats__label">{t('Actions')}</div>
      <div className="cc__lighttext">{formatNumber(pledgeCompletions, 0)}</div>
    </div>
    <FootprintProfileControl
      footprint={footprint}
      footprintPublic={footprintPublic}
      canManage={canManageFootprint}
      formatNumber={formatNumber}
      onHideFootprint={onHideFootprint}
      onRestoreFootprint={onRestoreFootprint}
    />
    <div className="cc__stats__savings">
      <table className="cc__stats__savings_table">
        <thead>
          <tr>
            <th scope="col" />
            <th
              scope="col"
              title="Lbs CO2 equivalent saved"
              dangerouslySetInnerHTML={{ __html: t('Lbs CO<sub>2</sub>e saved') }}
            />
            {showDollarSavings && (
              <th scope="col" title={t('Estimated San Francisco bill savings')}>{t('$ Saved')}</th>
            )}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">{t('New actions')}</th>
            <td className="cc__lighttext">{formatNumber(score.savingsCO2Lbs, 0)}</td>
            {showDollarSavings && (
              <td className="cc__lighttext">{formatDollars(newDollarsSaved, formatNumber)}</td>
            )}
          </tr>
          <tr>
            <th scope="row">{t('Actions already taken')}</th>
            <td className="cc__lighttext">{formatNumber(score.takenCO2Lbs, 0)}</td>
            {showDollarSavings && (
              <td className="cc__lighttext">{formatDollars(takenDollarsSaved, formatNumber)}</td>
            )}
          </tr>
        </tbody>
      </table>
    </div>
    <UserScoreLevel
      score={score}
      scoreLevel={scoreLevel}
      nextScoreLevel={nextScoreLevel}
    />
  </div>
);

const mapStateToProps = (state: State, ownProps: ManualProps): Partial<Props> => {
  const competitionName = String((state.competition && state.competition.name) || '').toLowerCase();
  const showDollarSavings = competitionName === 'ccsf' || competitionName === 'sf' || competitionName === 'sfenvironment';
  const completions = ownProps.completionsSource === 'profile'
    ? state.profilePledgeCompletions
    : state.userPledgeCompletions;
  const split = estimateUserDollarSavings(state.competitionPledges, completions, ownProps.score);
  return {
    showDollarSavings,
    newDollarsSaved: split.newDollarsSaved,
    takenDollarsSaved: split.takenDollarsSaved
  };
};

export default compose(
  connect(mapStateToProps),
  withLocalization('userStats')
)(UserStats) as React.FunctionComponent<ManualProps>;
